import { Box, ListItemText, Stack } from "@mui/material";
import { Link as RouterLink } from "@tanstack/react-router";
import Iconify from "src/components/Iconify";
import useLocales from "src/hooks/useLocales";

import { isExternalLink } from "..";
import { NavItemProps } from "../type";
import { ListItemIconStyle, ListItemStyle, ListItemTextStyle } from "./style";

// ----------------------------------------------------------------------

export function NavItemRoot({ item, isCollapse, open = false, active, onOpen }: NavItemProps) {
	const { title, path, icon, info, children } = item;

	const { translate } = useLocales();

	const renderContent = (
		<>
			{icon && <ListItemIconStyle>{icon}</ListItemIconStyle>}
			<ListItemTextStyle disableTypography isCollapse={isCollapse} primary={translate(title)} />
			{!isCollapse && (
				<>
					{info && info}
					{children && children.length > 0 && <ArrowIcon open={open} />}
					{/* {children && children.length === 0 && highlightAdd && <AddIcon />} */}
				</>
			)}
		</>
	);

	if (children && children.length !== 0) {
		return (
			<ListItemStyle activeRoot={active} onClick={onOpen}>
				{renderContent}
			</ListItemStyle>
		);
	}

	return isExternalLink(path) ? (
		<ListItemStyle component={RouterLink} href={path} rel='noopener' target='_blank'>
			{renderContent}
		</ListItemStyle>
	) : (
		<ListItemStyle activeRoot={active} component={RouterLink} to={path}>
			{renderContent}
		</ListItemStyle>
	);
}

// ----------------------------------------------------------------------

type NavItemSubProps = Omit<NavItemProps, "isCollapse">;

export function NavItemSub({ item, open = false, active = false, onOpen }: NavItemSubProps) {
	const { title, path, info, children } = item;
	const { translate } = useLocales();

	const renderContent = (
		<>
			<DotIcon active={active} />
			<Stack alignItems={"center"} flexDirection='row'>
				<ListItemText disableTypography primary={translate(title)} />
			</Stack>
			{info && info}
			{children && <ArrowIcon open={open} />}
		</>
	);

	if (children) {
		return (
			<ListItemStyle activeSub={active} onClick={onOpen} subItem>
				{renderContent}
			</ListItemStyle>
		);
	}

	return isExternalLink(path) ? (
		<ListItemStyle component={RouterLink} href={path} rel='noopener' subItem target='_blank'>
			{renderContent}
		</ListItemStyle>
	) : (
		<ListItemStyle activeSub={active} component={RouterLink} subItem to={path}>
			{renderContent}
		</ListItemStyle>
	);
}

// ----------------------------------------------------------------------

type DotIconProps = {
	active: boolean;
};

export function DotIcon({ active }: DotIconProps) {
	return (
		<ListItemIconStyle>
			<Box
				component='span'
				sx={{
					width: 4,
					height: 4,
					borderRadius: "50%",
					bgcolor: "text.disabled",
					transition: (theme) =>
						theme.transitions.create("transform", {
							duration: theme.transitions.duration.shorter
						}),
					...(active && {
						transform: "scale(2)",
						bgcolor: "primary.main"
					})
				}}
			/>
		</ListItemIconStyle>
	);
}

// ----------------------------------------------------------------------

type ArrowIconProps = {
	open: boolean;
};

export function ArrowIcon({ open }: ArrowIconProps) {
	return (
		<Iconify
			icon={open ? "eva:arrow-ios-downward-fill" : "eva:arrow-ios-forward-fill"}
			sx={{ width: 16, height: 16, ml: 1 }}
		/>
	);
}

// ----------------------------------------------------------------------

export function AddIcon() {
	return (
		<Iconify icon={"ic:round-add"} sx={{ width: 14, height: 14, ml: 1, color: "primary.main" }} />
	);
}
